import React, { useMemo } from "react";
import { css } from "@linaria/core";
import { useCallback, useState } from "react";
import t from "../../../../../i18n";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Modal from "@ui/Modal";
import ModalForm from "@ui/Modal/Form";
import RadioButton from "@ui/RadioButton";
import { Text } from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import useToasts from "@ui/Toast/useToasts";
import { useDashboard, useUpdateRemoteDashboardCallback } from "../../hook/api/dashboard";
import { useDashboardPermissions } from "../../hook/useDashboardPermissions";
import type { DashboardModelT } from "../../schema/dashboard";
import { useDashboardLinkedFaultEvents } from "../../../../../api/useFaultEventsDashboards";
import { useTranslations } from "../../../../../api/useTranslations";
import { useSummaryDashboards } from "../../../../../api/useSummaryDashboards";
import { indexBy } from "diagnostics-lib/iterable";
export function ModalManageSharing({
  opened,
  close,
  dashboard,
}: {
  opened: boolean;
  close: () => void;
  dashboard: DashboardModelT;
}) {
  const { addToast } = useToasts();
  const { blocks } = useDashboard(dashboard.projectId, dashboard.id);
  const linkedFaultEvents = useDashboardLinkedFaultEvents(dashboard.projectId, dashboard.id);
  const updateDashboard = useUpdateRemoteDashboardCallback();
  const [published, publish] = useState<boolean>(dashboard.published);
  const [readonly, setReadonly] = useState<boolean>(dashboard.readonly ?? false);
  const dashboardPermissions = useDashboardPermissions(dashboard);
  const translations = useTranslations();

  const manageSharing = useCallback(
    async () =>
      await updateDashboard({ ...dashboard, published, readonly }, blocks)
        .then(() => {
          addToast(
            "success",
            t("project.dashboard.transaction.manage_sharing.success", translations),
          );
          return true;
        })
        .catch(() => {
          addToast("error", t("project.dashboard.transaction.manage_sharing.error", translations));
          return false;
        }),
    [addToast, blocks, dashboard, published, readonly, updateDashboard, translations],
  );

  const summaryDashboards = useSummaryDashboards(dashboard.projectId);
  const indexedSummaryDashboards = useMemo(
    () => indexBy(summaryDashboards, "dashboardId"),
    [summaryDashboards],
  );
  const isLinkedToHome = indexedSummaryDashboards.has(dashboard.id);

  return (
    <Modal
      title={t("project.dashboard.actions.manage_sharing")}
      isOpen={opened}
      size="l"
      onRequestClose={close}
    >
      <ModalForm
        onSubmit={manageSharing}
        onRequestClose={close}
        okButtonName={t("project.dashboard.transaction.manage_sharing.validate")}
      >
        {dashboardPermissions.allowSharing && (
          <div className={formSection}>
            <>
              <Text type="body1SemiBold">
                {t("project.dashboard.transaction.manage_sharing.visibility.title")}
              </Text>
              <div className={fieldGroup}>
                <RadioButton checked={!published} onChange={() => publish(false)}>
                  {t("project.dashboard.transaction.manage_sharing.visibility.check.private")}
                </RadioButton>
                <RadioButton checked={published} onChange={() => publish(true)}>
                  {t("project.dashboard.transaction.manage_sharing.visibility.check.shared")}
                </RadioButton>
              </div>
              <Text type="body2" className={sharingManagementHint}>
                {published
                  ? t(
                      "project.dashboard.transaction.manage_sharing.visibility.hint.shared",
                      translations,
                    )
                  : t(
                      "project.dashboard.transaction.manage_sharing.visibility.hint.private",
                      translations,
                    )}
              </Text>
            </>
          </div>
        )}
        {published && dashboardPermissions.allowReadModeChange && (
          <div className={formSection}>
            <Text type="body1SemiBold">
              {t("project.dashboard.transaction.manage_sharing.access.title")}
            </Text>
            <div className={fieldGroup}>
              <RadioButton checked={readonly} onChange={() => setReadonly(true)}>
                {t("project.dashboard.transaction.manage_sharing.access.check.readonly")}
              </RadioButton>
              <RadioButton checked={!readonly} onChange={() => setReadonly(false)}>
                {t("project.dashboard.transaction.manage_sharing.access.check.readwrite")}
              </RadioButton>
            </div>
            <Text type="body2" className={sharingManagementHint}>
              {readonly
                ? t(
                    "project.dashboard.transaction.manage_sharing.access.hint.readonly",
                    translations,
                  )
                : t(
                    "project.dashboard.transaction.manage_sharing.access.hint.readwrite",
                    translations,
                  )}
            </Text>
          </div>
        )}
        {dashboard.published === true &&
          published === false &&
          (linkedFaultEvents.length > 0 || isLinkedToHome) && (
            <AdditionnalMessage
              status="WARNING"
              text={t(
                "project.dashboard.transaction.manage_sharing.share_to_private.message.linked_events",
                {
                  linkedEventCount: linkedFaultEvents.length,
                  isLinkedToHome,
                  translations,
                },
              )}
            />
          )}
        {dashboard.published === false &&
          published === true &&
          (linkedFaultEvents.length > 0 || isLinkedToHome) && (
            <AdditionnalMessage
              status="WARNING"
              text={t(
                "project.dashboard.transaction.manage_sharing.private_to_share.message.linked_events",
                {
                  linkedEventCount: linkedFaultEvents.length,
                  isLinkedToHome,
                  translations,
                },
              )}
            />
          )}
      </ModalForm>
    </Modal>
  );
}

const formSection = css`
  > * + * {
    margin-top: ${spacings.xs};
  }
`;

const fieldGroup = css`
  display: flex;
  gap: ${spacings.m};
  padding-left: 2px;
`;

const sharingManagementHint = css`
  color: ${colors.carbon700};
`;
